import {
  Badge,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useMantineTheme,
} from "@mantine/core";
import {
  IconBell,
  IconChartBar,
  IconCheck,
  IconDatabase,
  IconMessages,
  IconMicrophone,
  IconSearch,
} from "@tabler/icons-react";

interface ComparisonItem {
  before: string;
  after: string;
  icon: string;
}

const comparisons: ComparisonItem[] = [
  { before: "Text message threads", after: "Unified project dashboard", icon: "messages" },
  { before: "Paper daily logs", after: "Voice-powered logging", icon: "microphone" },
  { before: "Email attachments chaos", after: "Smart alerts & notifications", icon: "bell" },
  { before: '"Where\'s that photo?"', after: "Searchable photo library", icon: "search" },
  { before: "Month-end cost surprises", after: "Real-time budget tracking", icon: "chart" },
  { before: "Scattered spreadsheets", after: "Single source of truth", icon: "database" },
];

const metrics = {
  before: { time: "45 min/day", cost: "Unknown until month-end" },
  after: { time: "3 min/day", cost: "Live variance tracking" },
};

const iconMap: Record<string, React.ElementType> = {
  messages: IconMessages,
  microphone: IconMicrophone,
  bell: IconBell,
  search: IconSearch,
  chart: IconChartBar,
  database: IconDatabase,
};

interface BeforeAfterComparisonProps {
  embedded?: boolean;
}

export function BeforeAfterComparison({ embedded = false }: BeforeAfterComparisonProps) {
  const theme = useMantineTheme();

  return (
    <Stack gap="xl">
      {!embedded && (
        <Stack gap="xs" ta="center">
          <Title order={1}>The Kablan Difference</Title>
          <Text size="lg" c="dimmed">
            From chaos to clarity in 3 minutes a day
          </Text>
        </Stack>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
        {/* BEFORE Column */}
        <Card
          shadow="sm"
          padding="lg"
          radius="md"
          withBorder
          style={{ borderColor: theme.colors.gray[3] }}
        >
          <Stack gap="md">
            <Group justify="space-between">
              <Title order={3} c="dimmed">
                Before Kablan
              </Title>
              <Badge color="red" variant="light" size="lg">
                Old Way
              </Badge>
            </Group>

            <Stack gap="sm">
              {comparisons.map((item) => {
                const IconComponent = iconMap[item.icon];
                if (!IconComponent) return null;
                return (
                  <Group key={item.icon} align="flex-start" gap="sm">
                    <ThemeIcon size="md" radius="md" variant="light" color="gray">
                      <IconComponent size={16} />
                    </ThemeIcon>
                    <Text size="sm" c="dimmed" style={{ flex: 1 }}>
                      {item.before}
                    </Text>
                  </Group>
                );
              })}
            </Stack>

            <Card
              padding="md"
              radius="sm"
              withBorder
              style={{
                backgroundColor: theme.colors.gray[0],
                borderColor: theme.colors.gray[3],
              }}
            >
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm" fw={500} c="dimmed">
                    TIME:
                  </Text>
                  <Badge color="gray" variant="light">
                    {metrics.before.time}
                  </Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" fw={500} c="dimmed">
                    COST:
                  </Text>
                  <Badge color="gray" variant="light">
                    {metrics.before.cost}
                  </Badge>
                </Group>
              </Stack>
            </Card>
          </Stack>
        </Card>

        {/* AFTER Column */}
        <Card
          shadow="md"
          padding="lg"
          radius="md"
          withBorder
          style={{
            borderColor: theme.colors.green[5],
            borderWidth: 2,
          }}
        >
          <Stack gap="md">
            <Group justify="space-between">
              <Title order={3} c="green">
                With Kablan
              </Title>
              <Badge color="green" variant="filled" size="lg">
                New Way
              </Badge>
            </Group>

            <Stack gap="sm">
              {comparisons.map((item) => {
                const IconComponent = iconMap[item.icon];
                if (!IconComponent) return null;
                return (
                  <Group key={item.icon} align="flex-start" gap="sm">
                    <ThemeIcon size="md" radius="md" variant="light" color="green">
                      <IconCheck size={16} />
                    </ThemeIcon>
                    <ThemeIcon size="md" radius="md" variant="light" color="blue">
                      <IconComponent size={16} />
                    </ThemeIcon>
                    <Text size="sm" fw={500} style={{ flex: 1 }}>
                      {item.after}
                    </Text>
                  </Group>
                );
              })}
            </Stack>

            <Card
              padding="md"
              radius="sm"
              withBorder
              style={{
                backgroundColor: theme.colors.green[0],
                borderColor: theme.colors.green[4],
              }}
            >
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm" fw={600} c="green">
                    TIME:
                  </Text>
                  <Badge color="green" variant="filled" size="lg">
                    {metrics.after.time}
                  </Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" fw={600} c="green">
                    COST:
                  </Text>
                  <Badge color="green" variant="filled" size="lg">
                    {metrics.after.cost}
                  </Badge>
                </Group>
              </Stack>
            </Card>
          </Stack>
        </Card>
      </SimpleGrid>
    </Stack>
  );
}

export default BeforeAfterComparison;
